import React from 'react';
import ErrorMessage from './ErrorMessage';
import FaIcon from './FaIcon';

// CD-5241 mobile: the ≤768px card layout (bravo-app.css) wraps long values over
// several lines, so the default 50-char cap ballooned cells (e.g. Service /
// Opportunity names). Cap phones much lower — the full text stays a tap away
// behind the eye. Shared media query so every LongText re-renders on the same
// breakpoint change (rotation, resize) without each mounting its own query.
const MOBILE_MAX_LENGTH = 28;
const mobileQuery = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(mobileQuery ? mobileQuery.matches : false);
  React.useEffect(() => {
    if (!mobileQuery) return undefined;
    const onChange = (e) => setIsMobile(e.matches);
    mobileQuery.addEventListener('change', onChange);
    return () => mobileQuery.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const LongText = ({
  text = 'Sample text',
  maxLength = 50,
  type = 'tooltip',
  maxLengthForTooltip = 100,
  separator = undefined,
  withoutDots = false,
  fitToBox = false,
}) => {
  const isMobile = useIsMobile();

  // fitToBox: show the view-full eye only when the text actually overflows its box
  // (desktop single-line ellipsis → width overflow; the mobile popup title clamps
  // at 2 lines, OverWindow.css → height overflow). Fully visible text needs no eye.
  // Measured with the eye HIDDEN first, so toggling it on (which narrows the text
  // box and only increases overflow) cannot oscillate.
  const fitTextRef = React.useRef(null);
  const [fitOverflowing, setFitOverflowing] = React.useState(false);
  React.useLayoutEffect(() => {
    if (!fitToBox) return undefined;
    const el = fitTextRef.current;
    if (!el) return undefined;
    let disposed = false;
    // Tolerances: scroll* round up, client* round down, and browser zoom can skew
    // the rounded values by another px or two on a box that visually fits. A real
    // cut is big (ellipsis: several px of width; line-clamp: a whole ~20px line),
    // so lean generous — 2px width, 4px height.
    const measure = () => {
      if (disposed) return;
      setFitOverflowing(el.scrollWidth > el.clientWidth + 2 || el.scrollHeight > el.clientHeight + 4);
    };
    measure();
    // The mount-time measure sees the FALLBACK font: the popup titles render in
    // Oswald (condensed — narrower than the fallback), so a title that fits in
    // Oswald can measure as 3 fallback lines → stale "overflowing". The clamped
    // box doesn't change SIZE when the webfont swaps in (still 2 lines tall), so
    // the ResizeObserver below won't fire for it — re-measure once fonts settle.
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    return () => {
      disposed = true;
      if (observer) observer.disconnect();
    };
  }, [fitToBox, text]);

  const getMaxLength = () => {
    if (separator && text.indexOf(separator) > -1) {
      return text.indexOf(separator) + 1;
    }
    // respect a caller-passed cap that is already tighter than the mobile one
    return isMobile ? Math.min(maxLength, MOBILE_MAX_LENGTH) : maxLength;
  };

  if (typeof text !== 'string' || !text) return text;

  // fitToBox: no character cap — the full text goes in the DOM and CSS ellipsizes/
  // clamps it to the available box (.lt-fit rules), with the view-full eye at the
  // end only while the text is actually cut (fitOverflowing above). For captions in
  // width-constrained containers (detail popup titles) where a char count can't
  // know where the box ends.
  if (fitToBox) {
    // No m-1 here: Bootstrap's !important margin shifts the box while max-width
    // stays 100%, pushing the trailing eye past the container clip.
    return (
      <span className='lt-fit'>
        <span
          className='lt-text'
          ref={fitTextRef}
        >
          {text}
        </span>
        {fitOverflowing && (
          <FaIcon
            className='icon'
            iconName='fa-solid fa-eye'
            onClick={() => ErrorMessage.SetInfo(text)}
            tooltipText={maxLengthForTooltip < text.length ? `${text.substring(0, maxLengthForTooltip)}...` : text}
          />
        )}
      </span>
    );
  }
  const computedMaxLength = getMaxLength();
  if (computedMaxLength < 0) return 'Error in LongText component.';

  if (text.length <= computedMaxLength) return <span className='m-1'>{text}</span>;

  const resolvedType = type === 'tooltip' && text.length < maxLengthForTooltip ? 'tooltip' : 'info';

  const dots = withoutDots || computedMaxLength == 0 ? '' : '...';

  return (
    <span className='m-1'>
      {text.substring(0, computedMaxLength)}
      {dots}
      <FaIcon
        className={resolvedType === 'tooltip' ? 'text-secondary' : 'icon'}
        iconName='fa-solid fa-eye'
        onClick={() => ErrorMessage.SetInfo(text)}
        tooltipText={
          resolvedType === 'tooltip'
            ? text
            : maxLengthForTooltip < text.length
            ? `${text.substring(0, maxLengthForTooltip)}${dots}`
            : text
        }
      />
    </span>
  );
};

export default LongText;
